import type { SidebarConfig } from "./types";

/** Roots of accueilv2 feature pages (must match backend PAGE_CATALOG paths). */
export const FEATURE_PATH_ROOTS = [
  "/",
  "/appointments",
  "/meetings",
  "/concierge",
  "/cooperativeU/events",
  "/lending",
  "/messagerie",
  "/services",
  "/bar",
  "/events",
  "/gym",
  "/beauty",
  "/external-links",
  "/ticketing",
] as const;

/**
 * Utility / shell routes that stay reachable even if not in the sidebar
 * (header carts, notifications, widgets, profile subpages, …).
 */
export function isUtilityPath(requestPath: string): boolean {
  const path = (requestPath || "/").replace(/\/+$/, "") || "/";
  if (path === "/profile" || path.startsWith("/profile/")) return true;
  if (path === "/notifications" || path.startsWith("/notifications/")) return true;
  if (path === "/cart" || path.startsWith("/cart/")) return true;
  if (path === "/cartBar" || path.startsWith("/cartBar/")) return true;
  if (path === "/orders" || path.startsWith("/orders/")) return true;
  if (path === "/widgets" || path.startsWith("/widgets/")) return true;
  if (path === "/dashboard" || path === "/calendar") return true;
  if (path === "/auth-test") return true;
  return false;
}

export function isFeaturePath(requestPath: string): boolean {
  const path = (requestPath || "/").replace(/\/+$/, "") || "/";
  for (const root of FEATURE_PATH_ROOTS) {
    if (root === "/") {
      if (path === "/") return true;
      continue;
    }
    if (path === root || path.startsWith(`${root}/`)) return true;
  }
  return false;
}

/** Plus long préfixe FEATURE_PATH_ROOTS qui matche le chemin (hors `/`). */
export function featureRootFor(requestPath: string): string | null {
  const path = (requestPath || "/").replace(/\/+$/, "") || "/";
  let best: string | null = null;
  for (const root of FEATURE_PATH_ROOTS) {
    if (root === "/") continue;
    if (path === root || path.startsWith(`${root}/`)) {
      if (!best || root.length > best.length) best = root;
    }
  }
  return best;
}

export function isPathAllowed(
  allowedPaths: string[] | undefined,
  requestPath: string,
): boolean {
  if (isUtilityPath(requestPath)) return true;
  if (!isFeaturePath(requestPath)) return true;

  const normalized = (requestPath || "/").replace(/\/+$/, "") || "/";
  const paths = allowedPaths?.length ? allowedPaths : ["/"];

  // Cooperative U nested event flows share the same menu entry family.
  if (normalized.startsWith("/cooperativeU/")) {
    const hasCoop = paths.some((p) => p.startsWith("/cooperativeU/"));
    if (hasCoop) return true;
  }

  // Même feature root que l’entrée menu = toutes les sous-routes OK
  // (ex. /ticketing/report autorisé ⇒ /ticketing/all et /ticketing/assignments OK).
  const requestRoot = featureRootFor(normalized);
  if (requestRoot) {
    const hasFeature = paths.some((p) => featureRootFor(p) === requestRoot);
    if (hasFeature) return true;
  }

  for (const allowed of paths) {
    const base = (allowed || "/").replace(/\/+$/, "") || "/";
    if (normalized === base) return true;
    if (base !== "/" && normalized.startsWith(`${base}/`)) return true;
  }
  // Home is always OK as fallback landing
  if (normalized === "/") return true;
  return false;
}

export const EMPTY_SIDEBAR: SidebarConfig = {
  entreprisePk: 0,
  items: [],
  allowedPaths: ["/", "/profile"],
};
